import { AlertTriangle, XCircle } from "@untitledui/icons";
import type { ReactNode } from "react";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";
import type { ImportCSVRow } from "./types";
import type { DrawerState } from "./use-import-csv-state";
import { WrongFileFormatTable } from "./wrong-file-format-table";

// The handle in the CSV may or may not have a leading "@" — normalize both display lines
// so it's always shown consistently regardless of how the user typed it.
const stripAt = (handle: string) => handle.replace(/^@/, "");
const withAt = (handle: string) => `@${stripAt(handle)}`;

type ImportCsvDrawerProps<T extends ImportCSVRow> = {
  drawerState: DrawerState;
  rows: T[];
  selected: Set<string>;
  isSaving: boolean;
  // Optional extra content rendered after the name/handle block for each valid row —
  // e.g. an editable picker for a feature-specific field. This component stays
  // generic: it only places what the caller returns, with no knowledge of what it is.
  renderRowExtra?: (row: T) => ReactNode;
  columns: string[];
  className?: string;
  onDownloadTemplate: () => void | Promise<void>;
  onToggleRow: (handle: string) => void;
  onToggleAll: (checked: boolean) => void;
  onRequestClose: () => void;
  onKeepReviewing: () => void;
  onDiscard: () => void;
  onSave: () => void;
};

export const ImportCsvDrawer = <T extends ImportCSVRow>({
  drawerState,
  rows,
  selected,
  isSaving,
  renderRowExtra,
  columns,
  className,
  onDownloadTemplate,
  onToggleRow,
  onToggleAll,
  onRequestClose,
  onKeepReviewing,
  onDiscard,
  onSave,
}: ImportCsvDrawerProps<T>) => {
  const selectedCount = selected.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  if (drawerState === "wrong_file") {
    return (
      <SlideoutMenu isOpen onOpenChange={() => onRequestClose()} isDismissable className={className}>
        <SlideoutMenu.Header onClose={onRequestClose}>
          <div className="flex items-center gap-2">
            <XCircle className="w-6 h-6 text-error-primary" />
            <h2 className="text-lg font-semibold text-primary">Wrong file type</h2>
          </div>
        </SlideoutMenu.Header>
        <SlideoutMenu.Content className="flex flex-col gap-4">
          <p className="text-md text-tertiary">
            We couldn't find the columns we expected:{" "}
            <span className="font-semibold text-primary">{columns.join(", ")}</span>.
          </p>
          <WrongFileFormatTable columns={columns} />
          <p className="text-md text-primary">
            <button type="button" className="underline decoration-dotted text-brand" onClick={onDownloadTemplate}>
              Download the template
            </button>{" "}
            to match the format, then upload again.
          </p>
        </SlideoutMenu.Content>
        <SlideoutMenu.Footer className="flex justify-end">
          <Button color="primary" onClick={onRequestClose}>
            Close
          </Button>
        </SlideoutMenu.Footer>
      </SlideoutMenu>
    );
  }

  if (drawerState === "unsaved_import") {
    return (
      <SlideoutMenu isOpen onOpenChange={() => undefined} isDismissable={false} className={className}>
        <SlideoutMenu.Header onClose={onRequestClose}>
          <h2 className="text-lg font-semibold text-primary">Review Import</h2>
        </SlideoutMenu.Header>
        <SlideoutMenu.Content className="flex items-center justify-center">
          <div className="bg-[#fcf6f1] border border-border-secondary rounded-2xl px-8 pt-4 pb-8 flex flex-col items-center gap-2 w-full text-center">
            <div className="flex items-center justify-center rounded-full size-16">
              <AlertTriangle className="size-6 text-warning-600" />
            </div>
            <p className="text-xl font-semibold text-primary">Unsaved import</p>
            <p className="text-md text-tertiary w-[360px]">
              You haven't finished reviewing these users. If you leave now, this import won't be saved.
            </p>
          </div>
        </SlideoutMenu.Content>
        <SlideoutMenu.Footer className="flex gap-6">
          <button type="button" className="text-sm font-semibold text-tertiary" onClick={onDiscard}>
            Discard import
          </button>
          <Button color="secondary" className="flex-1" onClick={onKeepReviewing}>
            Keep reviewing
          </Button>
          <Button color="primary" className="flex-1" isLoading={isSaving} onClick={onSave}>
            Import
          </Button>
        </SlideoutMenu.Footer>
      </SlideoutMenu>
    );
  }

  return (
    <SlideoutMenu
      isOpen
      onOpenChange={() => onRequestClose()}
      isDismissable
      dialogClassName="overflow-hidden"
      className={className}
    >
      <SlideoutMenu.Header onClose={onRequestClose}>
        <h2 className="text-lg font-semibold text-primary">Review Import</h2>
      </SlideoutMenu.Header>
      <SlideoutMenu.Content className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden px-0 md:px-0">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-secondary bg-primary px-6 py-3 text-sm text-tertiary">
          <Checkbox
            size="sm"
            isSelected={allSelected}
            isIndeterminate={someSelected}
            isDisabled={rows.length === 0}
            onChange={onToggleAll}
            aria-label="Select all"
          />
          <span>{selectedCount.toLocaleString()} selected</span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {rows.map((row) => {
            const checked = selected.has(row.handle);
            const extra = renderRowExtra?.(row);
            return (
              <div
                key={row.handle}
                role="button"
                tabIndex={0}
                onClick={() => onToggleRow(row.handle)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleRow(row.handle);
                  }
                }}
                className={cx(
                  "flex items-center gap-3 border-b border-secondary px-6 py-3 cursor-pointer hover:bg-primary_hover",
                )}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox size="sm" isSelected={checked} onChange={() => onToggleRow(row.handle)} />
                </div>
                <Avatar
                  size="sm"
                  src={row.avatarUrl}
                  alt={row.name ?? row.handle}
                  initials={(row.name ?? stripAt(row.handle)).charAt(0).toUpperCase()}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-primary">{row.name ?? stripAt(row.handle)}</span>
                  <span className="truncate text-xs text-tertiary">{withAt(row.handle)}</span>
                </div>
                {extra && (
                  <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                    {extra}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SlideoutMenu.Content>
      <SlideoutMenu.Footer className="flex flex-row justify-between gap-3">
        <Button color="secondary" onClick={onRequestClose}>
          Cancel
        </Button>
        <Button color="primary" isLoading={isSaving} isDisabled={selectedCount === 0} onClick={onSave}>
          Import ({selectedCount.toLocaleString()})
        </Button>
      </SlideoutMenu.Footer>
    </SlideoutMenu>
  );
};
